import type { ReflectionDto, SessionEventDto, SessionResultsDto } from '@lila/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { useBoard } from '../../../features/board/api';
import { ResultsView } from '../../../features/reflection/ResultsView';
import { SessionJournal } from '../../../features/reflection/SessionJournal';
import { errorKeyFor } from '../../../lib/errors';
import { isLocale } from '../../../lib/i18n';
import { trpcClient } from '../../../lib/trpc';

/** Spec 12.2 `/:lang/app/sessions/:id` — the session journal, and its results once ended. */
export const Route = createFileRoute('/$lang/app/sessions/$id')({ component: Page });

function Page(): React.ReactNode {
  const { lang, id } = Route.useParams();
  const { t } = useTranslation(['common', 'board']);
  const locale = isLocale(lang) ? lang : 'en';

  const session = useQuery({
    queryKey: ['session', id],
    queryFn: () => trpcClient.session.get.query({ sessionId: id }),
    retry: false,
  });

  const board = useBoard(locale, session.data?.boardId);

  const events = useQuery({
    queryKey: ['session', id, 'events'],
    /* The whole log: spec 6.2 puts a session at 50-200 events, so there is nothing to page. */
    queryFn: (): Promise<readonly SessionEventDto[]> =>
      trpcClient.session.events.query({ sessionId: id, afterSeq: 0, limit: 500 }),
    enabled: session.data !== undefined,
  });

  const participantId = session.data?.participants[0]?.id;

  const reflections = useQuery({
    queryKey: ['session', id, 'reflections', participantId],
    queryFn: (): Promise<readonly ReflectionDto[]> => {
      /* Narrowed here rather than asserted: `enabled` is a runtime guard, not a type one. */
      if (participantId === undefined) return Promise.resolve([]);
      return trpcClient.reflection.listForParticipant.query({ sessionId: id, participantId });
    },
    enabled: participantId !== undefined,
  });

  const results = useQuery({
    queryKey: ['session', id, 'results'],
    queryFn: (): Promise<SessionResultsDto> => trpcClient.session.results.query({ sessionId: id }),
    enabled: session.data !== undefined,
  });

  if (session.error !== null) {
    return <Alert tone="error">{t(errorKeyFor(session.error))}</Alert>;
  }

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-xl font-semibold text-ink">{t('board:journal.title')}</h1>

        {session.data?.status === 'active' && (
          <Link
            to="/$lang/solo/$sessionId"
            params={{ lang, sessionId: id }}
            className="text-sm text-accent underline-offset-4 hover:underline"
          >
            {t('board:session.title')}
          </Link>
        )}
      </header>

      {results.data !== undefined && <ResultsView results={results.data} />}

      <SessionJournal
        events={events.data ?? []}
        board={board.data}
        reflections={reflections.data ?? []}
      />
    </section>
  );
}
